"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface Question {
  id: string;
  textES: string;
  textEN: string;
  weight: number;
  options: {
    id: string;
    textES: string;
    textEN: string;
    score: number;
    color: "success" | "warning" | "error";
  }[];
}

const questions: Question[] = [
  {
    id: "q1_will",
    textES: "¿Tiene un testamento válido y actualizado en Panamá?",
    textEN: "Do you have a valid, up-to-date will in Panama?",
    weight: 25,
    options: [
      {
        id: "q1_opt1",
        textES: "Sí, actualizado en los últimos 3 años",
        textEN: "Yes, updated within the last 3 years",
        score: 10,
        color: "success",
      },
      {
        id: "q1_opt2",
        textES: "Sí, pero tiene más de 3 años",
        textEN: "Yes, but it's over 3 years old",
        score: 5,
        color: "warning",
      },
      {
        id: "q1_opt3",
        textES: "No tengo testamento",
        textEN: "I don't have a will",
        score: 1,
        color: "error",
      },
    ],
  },
  {
    id: "q2_assets",
    textES: "¿Tiene documentación clara de todos sus activos (propiedades, inversiones, cuentas)?",
    textEN: "Do you have clear documentation of all your assets (properties, investments, accounts)?",
    weight: 20,
    options: [
      {
        id: "q2_opt1",
        textES: "Sí, todo está documentado y organizado",
        textEN: "Yes, everything is documented and organized",
        score: 10,
        color: "success",
      },
      {
        id: "q2_opt2",
        textES: "Parcialmente — algunos activos documentados",
        textEN: "Partially — some assets documented",
        score: 5,
        color: "warning",
      },
      {
        id: "q2_opt3",
        textES: "No tengo un inventario completo",
        textEN: "I don't have a complete inventory",
        score: 1,
        color: "error",
      },
    ],
  },
  {
    id: "q3_beneficiaries",
    textES: "¿Están claramente designados los beneficiarios en todas sus cuentas y pólizas?",
    textEN: "Are beneficiaries clearly designated on all your accounts and policies?",
    weight: 20,
    options: [
      {
        id: "q3_opt1",
        textES: "Sí, todos actualizados",
        textEN: "Yes, all updated",
        score: 10,
        color: "success",
      },
      {
        id: "q3_opt2",
        textES: "Algunos sí, otros no estoy seguro",
        textEN: "Some yes, others I'm not sure",
        score: 5,
        color: "warning",
      },
      {
        id: "q3_opt3",
        textES: "No he designado beneficiarios",
        textEN: "I haven't designated beneficiaries",
        score: 1,
        color: "error",
      },
    ],
  },
  {
    id: "q4_powers",
    textES: "¿Tiene poderes legales vigentes (para decisiones financieras y de salud)?",
    textEN: "Do you have current powers of attorney (for financial and health decisions)?",
    weight: 20,
    options: [
      {
        id: "q4_opt1",
        textES: "Sí, ambos vigentes",
        textEN: "Yes, both current",
        score: 10,
        color: "success",
      },
      {
        id: "q4_opt2",
        textES: "Solo uno de los dos",
        textEN: "Only one of the two",
        score: 5,
        color: "warning",
      },
      {
        id: "q4_opt3",
        textES: "No tengo poderes legales",
        textEN: "I don't have powers of attorney",
        score: 1,
        color: "error",
      },
    ],
  },
  {
    id: "q5_communication",
    textES: "¿Su familia conoce sus deseos y sabe dónde encontrar los documentos importantes?",
    textEN: "Does your family know your wishes and where to find important documents?",
    weight: 15,
    options: [
      {
        id: "q5_opt1",
        textES: "Sí, todo está comunicado",
        textEN: "Yes, everything is communicated",
        score: 10,
        color: "success",
      },
      {
        id: "q5_opt2",
        textES: "Parcialmente — algunos saben",
        textEN: "Partially — some know",
        score: 5,
        color: "warning",
      },
      {
        id: "q5_opt3",
        textES: "No hemos hablado de esto",
        textEN: "We haven't discussed this",
        score: 1,
        color: "error",
      },
    ],
  },
];

interface FormData {
  email: string;
  answers: { [key: string]: number };
}

export default function CalculatorForm() {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    answers: {},
  });
  const [showResults, setShowResults] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAnswerChange = (questionId: string, score: number) => {
    setFormData({
      ...formData,
      answers: { ...formData.answers, [questionId]: score },
    });
  };

  const calculateScore = () => {
    let totalWeightedScore = 0;
    questions.forEach((q) => {
      const answer = formData.answers[q.id];
      if (answer !== undefined) {
        totalWeightedScore += (answer * q.weight) / 10;
      }
    });
    return (totalWeightedScore / 100) * 10;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate email sending
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const score = calculateScore();
    setFinalScore(score);
    setShowResults(true);
    setIsSubmitting(false);
  };

  const getScoreCategory = (score: number) => {
    if (score >= 8.0) {
      return {
        color: "success",
        label: "Bien Protegido",
        message: "Su legado está bien protegido. Podemos optimizar los detalles.",
      };
    } else if (score >= 5.0) {
      return {
        color: "warning",
        label: "Riesgos Moderados",
        message: "Tiene bases, pero hay brechas importantes.",
      };
    } else {
      return {
        color: "error",
        label: "Exposición Crítica",
        message: "Su patrimonio está expuesto. Necesita actuar pronto.",
      };
    }
  };

  const allQuestionsAnswered = questions.every((q) => formData.answers[q.id] !== undefined);

  if (showResults) {
    const category = getScoreCategory(finalScore);
    const bgColor =
      category.color === "success" ?"bg-success/10 border-success/30"
        : category.color === "warning" ?"bg-warning/10 border-warning/30" :"bg-error/10 border-error/30";
    const textColor =
      category.color === "success" ?"text-success"
        : category.color === "warning" ?"text-warning" :"text-error";

    return (
      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`${bgColor} border-2 rounded-2xl p-10 text-center mb-12`}>
            <div className={`text-7xl font-serif mb-4 ${textColor}`}>
              {finalScore.toFixed(1)}/10
            </div>
            <div className="text-2xl font-semibold text-foreground mb-4">
              {category.label}
            </div>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {category.message}
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 text-center mb-12">
            <Icon name="EnvelopeIcon" size={48} className="text-secondary mx-auto mb-4" />
            <h3 className="text-2xl text-primary font-serif mb-4">
              Revise su correo para el análisis completo
            </h3>
            <p className="text-muted-foreground mb-6">
              Hemos enviado un desglose detallado de su evaluación a <strong>{formData.email}</strong>
            </p>
            <p className="text-sm text-muted-foreground italic">
              Si no ve el correo en unos minutos, revise su carpeta de spam.
            </p>
          </div>

          <div className="bg-primary text-primary-foreground rounded-2xl p-10 text-center">
            <h3 className="text-3xl font-serif mb-4">
              Agendar Consulta
            </h3>
            <p className="text-primary-foreground/80 mb-8 leading-relaxed">
              Nuestro equipo puede ayudarle a cerrar las brechas identificadas y proteger su legado.
            </p>
            <button className="bg-secondary hover:bg-secondary/90 text-secondary-foreground px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all shadow-xl inline-flex items-center gap-3">
              <Icon name="CalendarIcon" size={18} />
              Programar Reunión
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-background">
      <div className="max-w-3xl mx-auto px-6">
        <form onSubmit={handleSubmit} className="space-y-12">
          {/* Email Input */}
          <div className="bg-card border border-border rounded-2xl p-8">
            <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-3">
              Correo Electrónico <span className="text-error">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-4">
              Recibirá el análisis completo en este correo
            </p>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              placeholder="su.correo@ejemplo.com"
            />
          </div>

          {/* Questions */}
          {questions.map((question, qIdx) => (
            <div key={question.id} className="bg-card border border-border rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center text-secondary font-bold shrink-0">
                  {qIdx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {question.textES}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Peso: {question.weight}% del puntaje total
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {question.options.map((option) => {
                  const isSelected = formData.answers[question.id] === option.score;
                  const borderColor =
                    option.color === "success" ?"border-success"
                      : option.color === "warning" ?"border-warning" :"border-error";
                  const bgColor =
                    option.color === "success" ?"bg-success/5"
                      : option.color === "warning" ?"bg-warning/5" :"bg-error/5";

                  return (
                    <label
                      key={option.id}
                      className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? `${borderColor} ${bgColor}`
                          : "border-border hover:border-muted-foreground/30"
                      }`}
                    >
                      <input
                        type="radio"
                        name={question.id}
                        value={option.score}
                        checked={isSelected}
                        onChange={() => handleAnswerChange(question.id, option.score)}
                        className="mt-1 w-5 h-5 accent-secondary cursor-pointer"
                      />
                      <span className="text-sm text-foreground flex-1">
                        {option.textES}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!formData.email || !allQuestionsAnswered || isSubmitting}
            className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground py-5 px-6 rounded-full font-bold text-base uppercase tracking-wider transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                Calculando...
              </>
            ) : (
              <>
                Ver Mi Puntaje
                <Icon name="ChartBarIcon" size={20} />
              </>
            )}
          </button>

          {!allQuestionsAnswered && formData.email && (
            <p className="text-center text-sm text-muted-foreground">
              Por favor responda todas las preguntas para ver su puntaje
            </p>
          )}
        </form>
      </div>
    </section>
  );
}